import React, { useRef, useEffect } from 'react';
import s from './s.module.less';
import { useFileUpload, FileUploadResult } from 'hooks/useFileUpload';
import { Spin, Input, Button, message } from 'antd';
import { TFile } from 'types/inbox';
import { getProviderPrivateFileSigned, uploadProviderPrivateFile } from 'api/common';

const { TextArea } = Input;

interface IFileUploadResult extends TFile {
    id: number;
}

export interface FileTextAreaValue {
    text: string;
    files: FileUploadResult[];
}

interface Props {
    value?: FileTextAreaValue;
    onChange?: (value: FileTextAreaValue) => void;
    onSend?: (value: FileTextAreaValue) => void;
    placeholder?: string;
    disabled?: boolean;
    maxFiles?: number;
}

const FileTextArea: React.FC<Props> = ({
    value = { text: '', files: [] },
    onChange,
    onSend,
    placeholder = '',
    disabled = false,
    maxFiles = 5,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { loading, uploadFile } = useFileUpload({
        accept: '.pdf,.jpg,.jpeg,.png,.gif,.webp',
        maxSize: 10,
        formData: {
            uploadTypeEnum: 'PROVIDER_PRIVATE',
        },
        uploadFn: uploadProviderPrivateFile,
        formatFileData: async (result: IFileUploadResult) => {
            const data: FileUploadResult = {
                id: result.id,
                url: result.fileUri,
                name: result.fileOriginName,
                type: result.fileExtension,
            }

            const res = await getProviderPrivateFileSigned(result.fileUri);
            if (!res.error) {
                data.previewUrl = res.data?.data?.singedUrl;
            }
            return data;
        },
    });

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.({
            ...value,
            text: e.target.value,
        });
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        // 检查文件数量限制
        if (value.files.length + selectedFiles.length > maxFiles) {
            message.error(`You can only upload ${maxFiles} files at a time`);
            return;
        }

        // 并行上传所有文件
        const uploadPromises = Array.from(selectedFiles).map(async (file) => {
            const result = await uploadFile(file);
            return result;
        });

        const uploadedFiles = (await Promise.all(uploadPromises)).filter((f): f is FileUploadResult => f !== null);

        console.log('uploadedFiles', uploadedFiles);

        if (uploadedFiles.length > 0) {
            onChange?.({
                ...value,
                files: [...value.files, ...uploadedFiles],
            });
        }

        // 清空 input 以便重复选择同一文件
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (fileId: number) => {
        onChange?.({
            ...value,
            files: value.files.filter((f) => f.id !== fileId),
        });
    };

    const handleAttachClick = () => {
        if (disabled || loading) return;
        fileInputRef.current?.click();
    };

    const handleSend = () => {
        if (disabled || (!value.text.trim() && value.files.length === 0)) return;
        onSend?.(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // 如果按下 Enter 且没有 Shift，则发送消息
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // 阻止默认的换行行为
            handleSend();
        }
        // Shift + Enter 允许换行（不阻止默认行为）
    };

    const hasContent = value.text.trim() || value.files.length > 0;

    return (
        <div className={`${s.fileTextArea} ${disabled ? s.disabled : ''}`}>
            {/* 文件预览区域 */}
            {value.files.length > 0 && (
                <div className={s.filesPreview}>
                    {value.files.map((file) => (
                        <div key={file.id} className={s.fileItem}>
                            {file.type === 'pdf' ? (
                                <div className={s.pdfTag}>
                                    <svg className={s.pdfIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M9.33333 1.33334H4.00001C3.6464 1.33334 3.30724 1.47382 3.05719 1.72387C2.80715 1.97392 2.66667 2.31305 2.66667 2.66668V13.3333C2.66667 13.687 2.80715 14.0261 3.05719 14.2762C3.30724 14.5262 3.6464 14.6667 4.00001 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2762C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33334L9.33333 1.33334Z" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9.33333 1.33334V5.33334H13.3333" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className={s.pdfName}>{file.name}</span>
                                    <Button
                                        className={s.removeBtn}
                                        onClick={() => handleRemoveFile(file.id!)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Button>
                                </div>
                            ) : (
                                <div className={s.imagePreview}>
                                    <img src={file.previewUrl} alt={file.name} />
                                    <Button
                                        className={s.imageRemoveBtn}
                                        onClick={() => handleRemoveFile(file.id!)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 3L3 9M3 3L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* 文本输入区域 */}
            <TextArea
                className={s.textInput}
                value={value.text}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                autoSize
            />

            {/* 工具栏 */}
            <div className={s.toolbar}>
                <Button
                    className={s.attachBtn}
                    onClick={handleAttachClick}
                    disabled={disabled || loading}
                    aria-label="Attach file"
                >
                    {loading ? (
                        <Spin size="small" />
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
                            <g clipPath="url(#clip0_1733_8953)">
                                <path d="M15.6781 11.1926V20.5063C15.6781 21.6109 16.5735 22.5063 17.6781 22.5063C18.7827 22.5063 19.6781 21.6109 19.6781 20.5063L19.5568 11.3139C19.5568 9.10478 17.7659 7.31392 15.5568 7.31392C13.3477 7.31392 11.5568 9.10479 11.5568 11.3139L11.6781 20.5063C11.6781 23.82 14.3644 26.5063 17.6781 26.5063C20.9918 26.5063 23.6781 23.82 23.6781 20.5063L23.6885 11.6675" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_1733_8953">
                                    <rect width="24" height="24" fill="white" transform="translate(0 16.9707) rotate(-45)" />
                                </clipPath>
                            </defs>
                        </svg>
                    )}
                </Button>

                <Button
                    className={`${s.sendBtn} ${hasContent ? s.active : ''}`}
                    onClick={handleSend}
                    disabled={disabled || !hasContent}
                >
                    {disabled || !hasContent ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M20.2346 5.68609C20.6666 4.49109 19.5086 3.33309 18.3136 3.76609L3.70855 9.04809C2.50955 9.48209 2.36455 11.1181 3.46755 11.7571L8.12955 14.4561L12.2926 10.2931C12.4812 10.1109 12.7338 10.0101 12.996 10.0124C13.2582 10.0147 13.509 10.1199 13.6944 10.3053C13.8798 10.4907 13.9849 10.7415 13.9872 11.0037C13.9895 11.2659 13.8887 11.5185 13.7066 11.7071L9.54355 15.8701L12.2436 20.5321C12.8816 21.6351 14.5176 21.4891 14.9516 20.2911L20.2346 5.68609Z" fill="#9CA3AF" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M20.2346 5.68609C20.6666 4.49109 19.5086 3.33309 18.3136 3.76609L3.70855 9.04809C2.50955 9.48209 2.36455 11.1181 3.46755 11.7571L8.12955 14.4561L12.2926 10.2931C12.4812 10.1109 12.7338 10.0101 12.996 10.0124C13.2582 10.0147 13.509 10.1199 13.6944 10.3053C13.8798 10.4907 13.9849 10.7415 13.9872 11.0037C13.9895 11.2659 13.8887 11.5185 13.7066 11.7071L9.54355 15.8701L12.2436 20.5321C12.8816 21.6351 14.5176 21.4891 14.9516 20.2911L20.2346 5.68609Z" fill="#0E62F4" />
                        </svg>
                    )}
                </Button>
            </div>

            {/* 隐藏的文件输入 */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
        </div>
    );
};

export default FileTextArea;
